import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/Home.css';

// Types for feature items
interface FeatureItem {
  id: number;
  title: string;
  description: string;
  icon: string;
  link: string;
}

// Types for steps
interface StepItem {
  id: number;
  title: string;
  description: string;
  icon: string;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  
  // Feature items data
  const features: FeatureItem[] = [
    {
      id: 1,
      title: "Personalized Hormone Therapy",
      description: "Our bioidentical hormone therapies are custom-formulated for your unique body chemistry and specific symptoms to restore optimal balance.",
      icon: "flask",
      link: "/services"
    },
    {
      id: 2,
      title: "Comprehensive Wellness",
      description: "We address not just hormones, but your total well-being with integrated nutritional guidance, stress management, and lifestyle optimization.",
      icon: "heart-pulse",
      link: "/approach"
    },
    {
      id: 3,
      title: "Expert Care Team",
      description: "Our board-certified specialists have extensive experience in hormone health and provide compassionate care at every step of your journey.",
      icon: "user-md",
      link: "/about"
    }
  ];

  // Step items data
  const steps: StepItem[] = [
    {
      id: 1,
      title: "Personalized Consultation",
      description: "Begin with a thorough evaluation of your symptoms, health history, and wellness goals with our hormone specialist in our peaceful, spa-like setting.",
      icon: "comments"
    },
    {
      id: 2,
      title: "Comprehensive Testing",
      description: "Undergo our advanced hormone panel that measures not just standard levels but the full spectrum of hormones and related biomarkers affecting your well-being.",
      icon: "vial"
    },
    {
      id: 3,
      title: "Custom Treatment Plan",
      description: "Receive your personalized bioidentical hormone therapy protocol, precisely formulated to address your specific imbalances and unique health needs.",
      icon: "clipboard-check"
    },
    {
      id: 4,
      title: "Ongoing Support & Refinement",
      description: "Experience dedicated care with regular follow-ups to fine-tune your treatment, celebrate your progress, and optimize your results as your body responds.",
      icon: "chart-line"
    }
  ];

  // Home page specific schema
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "VitalBalanceHormones",
    "url": "https://vitalbalancehormones.com",
    "logo": "https://vitalbalancehormones.com/logo512.png",
    "description": "Personalized bioidentical hormone therapy and wellness solutions for women experiencing menopause, perimenopause, and hormonal imbalances.",
    "medicalSpecialty": ["Hormone Therapy", "Women's Health"],
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://vitalbalancehormones.com/booking",
        "inLanguage": "en-US",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/IOSPlatform",
          "http://schema.org/AndroidPlatform"
        ]
      },
      "result": {
        "@type": "Reservation",
        "name": "Hormone Therapy Consultation"
      }
    }
  };

  // Handle navigation
  const handleLearnMore = () => {
    navigate('/services');
  };

  const handleStartJourney = () => {
    navigate('/services');
  };

  return (
    <div className="home-page">
      <SEO 
        title="VitalBalanceHormones | Bioidentical Hormone Therapy & Women's Wellness"
        description="Restore hormonal balance and reclaim your vitality with personalized bioidentical hormone therapy. Expert treatment for menopause, perimenopause, and hormonal imbalances."
        keywords="bioidentical hormone therapy, women's hormone health, menopause treatment, perimenopause symptoms, hormone replacement therapy, BHRT, hormone imbalance, wellness solutions"
        url="/"
        schema={homeSchema}
      />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-main-title">
            <span className="desktop-title">Balance Begins Within. Feel Like You Again — Naturally.</span>
            <span className="mobile-title">
              Balance Begins Within.<br />
              Feel Like You Again<br />
              — Naturally.
            </span>
          </h1>
          <p className="hero-secondary-title">Organic Bioidentical Hormones</p>
          <p className="hero-tagline">Safe - Precise - Life Changing</p>
          <p className="hero-subtitle">
            Experience the transformative power of VitalBalanceHormones' personalized bioidentical hormone therapies designed specifically for women's unique needs.
          </p>
          <div className="hero-buttons">
            <button className="secondary-button" onClick={handleLearnMore}>
              <i className="icon-info-circle button-icon"></i>
              Learn More
            </button>
          </div>
        </div>
        <div className="wave-divider">
          <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
          </svg>
        </div>
      </section>

      {/* Why Hormones Matter Section */}
      <section className="hormone-info-section">
        <div className="section-container">
          <h2 className="section-title">Why Hormones Matter</h2>
          <p className="section-subtitle primary-text">
            Hormones are essential messengers in the body that control mood, metabolism, sleep, libido, and more. As women approach menopause, hormone levels naturally decline—causing a wide range of symptoms that disrupt everyday life. Bioidentical hormone therapy offers a safe, personalized way to rebalance your system and restore your wellness.
          </p>
          
          <div className="symptoms-grid">
            {/* Symptoms Cards */}
            <div className="symptom-card">
              <h4>Hair Loss</h4>
            </div>
            <div className="symptom-card">
              <h4>Weight Gain</h4>
            </div>
            <div className="symptom-card">
              <h4>Brain Fog</h4>
            </div>
            <div className="symptom-card">
              <h4>Sleep Issues</h4>
            </div>
            <div className="symptom-card">
              <h4>Irritability</h4>
            </div>
            <div className="symptom-card">
              <h4>Loss of Libido</h4>
            </div>
            <div className="symptom-card">
              <h4>Hot Flashes</h4>
            </div>
            <div className="symptom-card">
              <h4>Night Sweats</h4>
            </div>
            <div className="symptom-card">
              <h4>Mood Swings</h4>
            </div>
          </div>
          
          <div className="hormone-cta">
            <button className="primary-button" onClick={handleStartJourney}>
              Start Your Wellness Journey
            </button>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="intro-section">
        <div className="section-container">
          <h2 className="section-title">Your Wellness Journey</h2>
          <p className="section-subtitle">
            At VitalBalanceHormones, we understand that hormone imbalances affect every aspect of your well-being.
            Our science-based approach helps women restore natural balance in a serene, supportive environment.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-container">
          <h2 className="section-title">Our Unique Approach</h2>
          <p className="section-subtitle">
            Experience our comprehensive, personalized hormone therapy solutions designed to address your specific needs and restore your quality of life.
          </p>
          <div className="feature-cards">
            {features.map((feature) => (
              <div key={feature.id} className="feature-card">
                <div className="feature-icon">
                  <i className={`icon-${feature.icon}`}></i>
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <a href={feature.link} className="feature-link">
                  Learn more <i className="icon-arrow-right"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-container">
          <h2 className="section-title">Your Wellness Journey</h2>
          <p className="section-subtitle">
            Our comprehensive process ensures you receive the most effective treatment tailored to your unique hormonal profile.
          </p>
          <div className="steps-container">
            {steps.map((step) => (
              <div key={step.id} className="step-item">
                <div className="step-number">{step.id}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Overview & SEO Boost Section */}
      <section className="business-overview-section">
        <div className="section-container">
          <div className="business-overview-header">
            <h2 className="section-title">Leading Bioidentical Hormone Therapy Specialists</h2>
            <p className="section-subtitle">
              VitalBalanceHormones is Colorado's premier destination for personalized women's hormone health, 
              combining cutting-edge medical expertise with compassionate, individualized care.
            </p>
          </div>

          <div className="business-overview-grid">
            {/* Our Expertise */}
            <div className="overview-block">
              <h3>Our Medical Expertise</h3>
              <div className="expertise-content">
                <div className="expertise-item">
                  <i className="icon-user-md expertise-icon"></i>
                  <div className="expertise-text">
                    <h4>Board-Certified Specialists</h4>
                    <p>Our team is led by Kelly Buckley, PA-C, a certified physician assistant with specialized training in women's hormone health and bioidentical hormone replacement therapy.</p>
                  </div>
                </div>
                <div className="expertise-item">
                  <i className="icon-microscope expertise-icon"></i>
                  <div className="expertise-text">
                    <h4>Advanced Diagnostic Testing</h4>
                    <p>We utilize comprehensive hormone panels, DUTCH testing, and specialized biomarker analysis to create precise treatment protocols tailored to your unique physiology.</p>
                  </div>
                </div>
                <div className="expertise-item">
                  <i className="icon-shield-check expertise-icon"></i>
                  <div className="expertise-text">
                    <h4>Evidence-Based Treatment</h4>
                    <p>Our protocols are based on the latest peer-reviewed research in hormone therapy, ensuring safe, effective treatment with optimal outcomes.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Conditions We Treat */}
            <div className="overview-block">
              <h3>Conditions We Specialize In</h3>
              <div className="conditions-grid">
                <div className="condition-category">
                  <h4>Menopause & Perimenopause</h4>
                  <ul>
                    <li>Hot flashes and night sweats</li>
                    <li>Mood changes and irritability</li>
                    <li>Sleep disturbances</li>
                    <li>Vaginal dryness and discomfort</li>
                    <li>Decreased libido</li>
                  </ul>
                </div>
                <div className="condition-category">
                  <h4>Hormone Imbalances</h4>
                  <ul>
                    <li>Estrogen dominance</li>
                    <li>Progesterone deficiency</li>
                    <li>Low testosterone in women</li>
                    <li>Thyroid dysfunction</li>
                    <li>Adrenal fatigue</li>
                  </ul>
                </div>
                <div className="condition-category">
                  <h4>Metabolic Health</h4>
                  <ul>
                    <li>Weight gain and difficulty losing weight</li>
                    <li>Insulin resistance</li>
                    <li>Metabolic syndrome</li>
                    <li>Energy and fatigue issues</li>
                    <li>Brain fog and cognitive decline</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Treatment Modalities */}
            <div className="overview-block">
              <h3>Our Specialized Treatments</h3>
              <div className="treatment-modalities">
                <div className="modality-item">
                  <h4>Peptide Therapy</h4>
                  <p><strong>Active Molecules:</strong> Arginine, Taurine (amino acids)</p>
                  <p><strong>Effect & Use:</strong> Boosts energy, metabolism, muscle recovery</p>
                </div>
                <div className="modality-item">
                  <h4>Mounjaro® Injection</h4>
                  <p><strong>Active Molecule:</strong> Tirzepatide (GLP‑1 analog)</p>
                  <p><strong>Effect & Use:</strong> Helps control blood sugar, aids weight loss</p>
                </div>
                <div className="modality-item">
                  <h4>Ozempic® Injection</h4>
                  <p><strong>Active Molecule:</strong> Semaglutide (GLP‑1 analog)</p>
                  <p><strong>Effect & Use:</strong> Mimics GLP‑1 to regulate insulin & digestion</p>
                </div>
                <div className="modality-item">
                  <h4>Bioidentical Hormone Replacement Therapy (BHRT)</h4>
                  <p>Customized hormone formulations including estradiol, progesterone, testosterone, and thyroid hormones delivered through creams, pellets, or oral preparations.</p>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="overview-block">
              <h3>Why Women Choose VitalBalanceHormones</h3>
              <div className="why-choose-grid">
                <div className="why-choose-item">
                  <i className="icon-heart why-choose-icon"></i>
                  <h4>Personalized Care</h4>
                  <p>Every treatment plan is uniquely designed based on your individual symptoms, lab results, and health goals.</p>
                </div>
                <div className="why-choose-item">
                  <i className="icon-clock why-choose-icon"></i>
                  <h4>Extended Consultations</h4>
                  <p>We take the time needed for thorough evaluations and detailed discussions about your health concerns.</p>
                </div>
                <div className="why-choose-item">
                  <i className="icon-phone why-choose-icon"></i>
                  <h4>Ongoing Support</h4>
                  <p>Direct access to your provider and regular monitoring to ensure optimal treatment outcomes.</p>
                </div>
                <div className="why-choose-item">
                  <i className="icon-spa why-choose-icon"></i>
                  <h4>Spa-Like Environment</h4>
                  <p>Experience hormone therapy in a peaceful, relaxing setting designed for your comfort and well-being.</p>
                </div>
                <div className="why-choose-item">
                  <i className="icon-graduation-cap why-choose-icon"></i>
                  <h4>Patient Education</h4>
                  <p>Comprehensive education about your hormone health empowers you to make informed decisions about your care.</p>
                </div>
                <div className="why-choose-item">
                  <i className="icon-map-marker why-choose-icon"></i>
                  <h4>Convenient Location</h4>
                  <p>Easily accessible location in Littleton, Colorado, serving the Denver metro area and surrounding communities.</p>
                </div>
              </div>
            </div>

            {/* Treatment Outcomes */}
            <div className="overview-block">
              <h3>Expected Treatment Benefits</h3>
              <div className="outcomes-showcase">
                <div className="outcome-category">
                  <h4>Physical Improvements</h4>
                  <div className="outcome-items">
                    <span className="outcome-tag">Increased Energy</span>
                    <span className="outcome-tag">Better Sleep</span>
                    <span className="outcome-tag">Weight Management</span>
                    <span className="outcome-tag">Improved Skin Health</span>
                    <span className="outcome-tag">Enhanced Libido</span>
                    <span className="outcome-tag">Stronger Bones</span>
                  </div>
                </div>
                <div className="outcome-category">
                  <h4>Mental & Emotional Wellness</h4>
                  <div className="outcome-items">
                    <span className="outcome-tag">Mood Stability</span>
                    <span className="outcome-tag">Mental Clarity</span>
                    <span className="outcome-tag">Reduced Anxiety</span>
                    <span className="outcome-tag">Improved Focus</span>
                    <span className="outcome-tag">Emotional Balance</span>
                    <span className="outcome-tag">Increased Confidence</span>
                  </div>
                </div>
                <div className="outcome-category">
                  <h4>Quality of Life</h4>
                  <div className="outcome-items">
                    <span className="outcome-tag">Better Relationships</span>
                    <span className="outcome-tag">Work Performance</span>
                    <span className="outcome-tag">Social Engagement</span>
                    <span className="outcome-tag">Overall Vitality</span>
                    <span className="outcome-tag">Life Satisfaction</span>
                    <span className="outcome-tag">Renewed Purpose</span>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;